var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import DebugService from './DebugService';
import OfficeIdConverterService from './OfficeIdConverterService';
import OfficeModeService from './OfficeModeService';
class GraphEmailService {
    constructor() { }
    static getInstance() {
        if (!GraphEmailService.instance) {
            GraphEmailService.instance = new GraphEmailService();
        }
        return GraphEmailService.instance;
    }
    getAuthHeaders(graphToken) {
        return {
            'Authorization': `Bearer ${graphToken}`,
            'Content-Type': 'application/json'
        };
    }
    /**
     * Debug the Graph token and user context
     */
    debugTokenAndUser(graphToken) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                // Decode the token to see user information (basic JWT decode)
                const tokenParts = graphToken.split('.');
                if (tokenParts.length === 3) {
                    const payload = JSON.parse(atob(tokenParts[1]));
                    DebugService.debug('Token payload:', {
                        sub: payload.sub,
                        oid: payload.oid,
                        upn: payload.upn,
                        email: payload.email,
                        aud: payload.aud,
                        iss: payload.iss
                    });
                }
                // Try to get user info from Graph API
                const userInfoUrl = 'https://graph.microsoft.com/v1.0/me';
                const headers = this.getAuthHeaders(graphToken);
                const userRes = yield fetch(userInfoUrl, {
                    method: 'GET',
                    headers
                });
                if (userRes.ok) {
                    const userInfo = yield userRes.json();
                    DebugService.debug('Graph API user info:', {
                        id: userInfo.id,
                        userPrincipalName: userInfo.userPrincipalName,
                        mail: userInfo.mail,
                        displayName: userInfo.displayName
                    });
                }
                else {
                    DebugService.warn('Failed to get user info from Graph API:', userRes.status);
                }
            }
            catch (error) {
                DebugService.warn('Error debugging token and user:', error);
            }
        });
    }
    /**
     * Get the correct Graph API endpoint based on mailbox type
     * @param mailboxEmail The email address of the mailbox (personal or shared)
     * @param isShared Whether the mailbox is shared
     * @returns The Graph API endpoint
     */
    getMailboxEndpoint(mailboxEmail, isShared) {
        if (isShared) {
            return `https://graph.microsoft.com/v1.0/users/${encodeURIComponent(mailboxEmail)}`;
        }
        else {
            return 'https://graph.microsoft.com/v1.0/me';
        }
    }
    forwardEmailWithGraphToken(graphToken, data, sourceMailboxEmail, isSourceShared) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            try {
                DebugService.service('GraphEmailService', 'forwardEmailWithGraphToken started');
                DebugService.object('Forward parameters', data);
                DebugService.debug(`Starting email forwarding for emailId: ${data.emailId}`);
                // Debug token and user context
                yield this.debugTokenAndUser(graphToken);
                const headers = this.getAuthHeaders(graphToken);
                // Step 1: Get the original email with attachments (with retry for timing)
                DebugService.debug('Step 1: Getting original email...');
                const originalEmail = yield this.getEmailWithRetry(graphToken, data.emailId, 0, sourceMailboxEmail, isSourceShared);
                // Only add delay for draft emails (not for sent emails)
                const isDraft = OfficeModeService.isComposeMode();
                if (isDraft) {
                    DebugService.debug('Draft email detected - waiting 5 seconds before creating draft...');
                    yield new Promise(resolve => setTimeout(resolve, 5000));
                }
                // Step 2: Create draft with attachments
                DebugService.debug('Step 2: Creating draft...');
                const draftBody = {
                    subject: `Ingestion Requested(${data.uwwbID}): ${originalEmail.subject || 'No Subject'}`,
                    body: originalEmail.body || {
                        contentType: "HTML",
                        content: "<p>Email forwarded via Workbench</p>"
                    },
                    toRecipients: [
                        {
                            emailAddress: {
                                address: data.sharedMailbox
                            }
                        }
                    ]
                };
                // Add attachments if they exist
                if (originalEmail.hasAttachments && originalEmail.attachments && originalEmail.attachments.length > 0) {
                    const attachments = [];
                    for (const attachment of originalEmail.attachments) {
                        if (attachment['@odata.type'] === '#microsoft.graph.fileAttachment') {
                            attachments.push({
                                '@odata.type': '#microsoft.graph.fileAttachment',
                                name: attachment.name,
                                contentType: attachment.contentType,
                                contentBytes: attachment.contentBytes,
                                size: attachment.size
                            });
                        }
                    }
                    if (attachments.length > 0) {
                        draftBody.attachments = attachments;
                    }
                }
                // Step 3: Create draft
                // Create the draft in the current user's mailbox (not the shared mailbox)
                // The shared mailbox will be the recipient, not the sender
                const currentUserEndpoint = 'https://graph.microsoft.com/v1.0/me';
                const draftUrl = `${currentUserEndpoint}/messages`;
                DebugService.debug(`Creating draft in current user's mailbox to send TO shared mailbox: ${data.sharedMailbox}`);
                DebugService.api('POST', draftUrl);
                DebugService.object('Draft body', draftBody);
                const draftRes = yield fetch(draftUrl, {
                    method: "POST",
                    headers: Object.assign(Object.assign({}, headers), { 'Content-Type': 'application/json' }),
                    body: JSON.stringify(draftBody)
                });
                if (!draftRes.ok) {
                    const err = yield draftRes.json();
                    throw new Error(`Failed to create draft: ${(_a = err.error) === null || _a === void 0 ? void 0 : _a.message}`);
                }
                const draft = yield draftRes.json();
                DebugService.debug(`Draft created successfully: ${draft.id}`);
                // Only add delay for draft emails before sending
                if (isDraft) {
                    DebugService.debug('Draft email detected - waiting 5 seconds before sending...');
                    yield new Promise(resolve => setTimeout(resolve, 5000));
                }
                // Step 4: Send the draft
                DebugService.debug(`Sending draft FROM current user's mailbox TO shared mailbox: ${data.sharedMailbox}`);
                const sendUrl = `${currentUserEndpoint}/messages/${draft.id}/send`;
                DebugService.api('POST', sendUrl);
                const sendRes = yield fetch(sendUrl, {
                    method: "POST",
                    headers
                });
                if (!sendRes.ok) {
                    const err = yield sendRes.json();
                    throw new Error(`Failed to send draft: ${(_b = err.error) === null || _b === void 0 ? void 0 : _b.message}`);
                }
                DebugService.debug('Email forwarded successfully with attachments');
                DebugService.email('Email forwarded successfully with attachments');
            }
            catch (error) {
                DebugService.errorWithStack('Email forwarding failed', error);
                throw error;
            }
        });
    }
    // Helper method to get email with retry for timing issues
    getEmailWithRetry(graphToken_1, emailId_1) {
        return __awaiter(this, arguments, void 0, function* (graphToken, emailId, retry = 0, mailboxEmail, isShared) {
            var _a, _b, _c, _d, _e;
            const headers = this.getAuthHeaders(graphToken);
            // Convert Exchange ID to REST ID for Graph API
            let restId;
            // Check if the ID is already a REST ID (contains no special characters)
            if (!emailId.includes('+') && !emailId.includes('/') && !emailId.includes('\\')) {
                DebugService.debug('ID appears to be already a REST ID, skipping conversion');
                restId = emailId;
            }
            else {
                try {
                    restId = yield OfficeIdConverterService.convertToRestId(emailId);
                    DebugService.debug(`ID Conversion successful - Original: "${emailId}", Converted: "${restId}"`);
                }
                catch (conversionError) {
                    DebugService.error('ID conversion failed:', conversionError);
                    restId = emailId; // Fallback to original ID
                }
            }
            // Debug logging to understand ID conversion issues
            DebugService.debug(`ID contains special chars - Original: ${/[\/\\]/.test(emailId)}, Converted: ${/[\/\\]/.test(restId)}`);
            DebugService.debug(`Mailbox info - mailboxEmail: "${mailboxEmail}", isShared: ${isShared}`);
            // Check if conversion failed or returned invalid ID
            if (!restId || restId === emailId) {
                DebugService.warn('ID conversion may have failed - using original emailId');
                // If conversion failed, the service should have logged a warning
                // We'll proceed with the original ID and let the API call fail gracefully
            }
            // Use the correct endpoint based on mailbox type
            const baseEndpoint = mailboxEmail && isShared !== undefined
                ? this.getMailboxEndpoint(mailboxEmail, isShared)
                : 'https://graph.microsoft.com/v1.0/me';
            const emailUrl = `${baseEndpoint}/messages/${restId}?$expand=attachments`;
            DebugService.debug(`Using endpoint: ${baseEndpoint} for email ID: ${restId}`);
            try {
                DebugService.api('GET', emailUrl);
                DebugService.debug(`Attempting to get email ${restId} (original: ${emailId}) (attempt ${retry + 1}/10)`);
                const emailRes = yield fetch(emailUrl, {
                    method: "GET",
                    headers,
                });
                if (!emailRes.ok) {
                    const err = yield emailRes.json();
                    if (((_a = err.error) === null || _a === void 0 ? void 0 : _a.code) === 'RequestBroker--ParseUri' && (emailRes.status === 500 || emailRes.status === 400)) {
                        DebugService.error(`Graph API returned 500 error for emailId: ${emailId}, restId: ${restId}`);
                        DebugService.error('500 error details:', err);
                        // If this is the first attempt and we have a 500 error, try internetMessageId fallback first
                        if (retry === 0) {
                            DebugService.warn('500 error on first attempt - trying internetMessageId fallback method');
                            try {
                                // Try to get internetMessageId from the current context
                                const { getInternetMessageId } = yield import('../utils/emailHelpers');
                                const internetMessageId = yield getInternetMessageId(Office.context.mailbox.item);
                                if (internetMessageId) {
                                    DebugService.debug('Found internetMessageId, attempting search:', internetMessageId);
                                    return yield this.getEmailByInternetMessageId(graphToken, internetMessageId, 0, mailboxEmail, isShared);
                                }
                                else {
                                    DebugService.warn('No internetMessageId available, trying folder search fallback');
                                    // Fall back to folder-specific search for drafts only
                                    if (OfficeModeService.isComposeMode()) {
                                        return yield this.getEmailBySearchFallback(graphToken, emailId, 0, mailboxEmail, isShared);
                                    }
                                }
                            }
                            catch (fallbackError) {
                                DebugService.warn('InternetMessageId fallback also failed:', fallbackError);
                                // Continue with normal retry logic
                            }
                        }
                    }
                    // Handle specific error cases
                    if (emailRes.status === 404 && retry < 10) {
                        const delay = 5000; // 5 seconds between each retry
                        DebugService.warn(`Email not found in Graph API, retrying in ${delay}ms... (attempt ${retry + 1}/10)`);
                        DebugService.warn(`404 Error details: ${JSON.stringify(err)}`);
                        // Try different approaches for 404 errors
                        if (retry === 0) {
                            DebugService.warn('First 404 attempt - trying alternative ID approaches');
                            // Try with original email ID (no conversion)
                            try {
                                DebugService.warn('Trying with original email ID (no conversion)');
                                const originalEmailUrl = `https://graph.microsoft.com/v1.0/me/messages/${emailId}?$expand=attachments`;
                                DebugService.api('GET', originalEmailUrl);
                                const originalEmailRes = yield fetch(originalEmailUrl, {
                                    method: "GET",
                                    headers,
                                });
                                if (originalEmailRes.ok) {
                                    const originalEmail = yield originalEmailRes.json();
                                    DebugService.debug('Email retrieved successfully using original ID');
                                    return originalEmail;
                                }
                                else {
                                    DebugService.warn(`Original ID also failed with status: ${originalEmailRes.status}`);
                                }
                            }
                            catch (originalError) {
                                DebugService.warn('Original ID approach failed:', originalError);
                            }
                            // Try with a longer delay for timing issues
                            DebugService.warn('Trying with extended delay for timing issues');
                            yield new Promise(resolve => setTimeout(resolve, 10000)); // 10 seconds
                        }
                        yield new Promise(resolve => setTimeout(resolve, delay));
                        return this.getEmailWithRetry(graphToken, emailId, retry + 1, mailboxEmail, isShared);
                    }
                    // Handle 400 errors with malformed ID
                    if (emailRes.status === 400 && ((_b = err.error) === null || _b === void 0 ? void 0 : _b.code) === 'ErrorInvalidIdMalformed') {
                        DebugService.error(`Malformed ID error - Original: "${emailId}", Converted: "${restId}"`);
                        DebugService.error('400 Malformed ID error details:', err);
                        // Try search fallback for malformed ID errors only if we're in draft mode
                        if (retry === 0 && OfficeModeService.isComposeMode()) {
                            DebugService.warn('Malformed ID error in draft mode - trying search fallback method');
                            try {
                                return yield this.getEmailBySearchFallback(graphToken, emailId, 0, mailboxEmail, isShared);
                            }
                            catch (fallbackError) {
                                DebugService.warn('Search fallback also failed:', fallbackError);
                                // Continue with normal retry logic
                            }
                        }
                    }
                    // Handle 400 errors with invalid mailbox item ID
                    if (emailRes.status === 400 && ((_c = err.error) === null || _c === void 0 ? void 0 : _c.code) === 'ErrorInvalidMailboxItemId') {
                        DebugService.error(`Invalid mailbox item ID error - Item doesn't belong to targeted mailbox`);
                        DebugService.error('400 Invalid Mailbox Item ID error details:', err);
                        // This could mean:
                        // 1. We're trying to access a shared mailbox email with /me/ endpoint
                        // 2. The email ID is corrupted or invalid
                        // 3. There's a timing issue with email ID conversion
                        if (retry === 0) {
                            DebugService.warn('Invalid mailbox item ID - trying alternative approaches');
                            // First, try with the personal mailbox endpoint but with different ID handling
                            try {
                                DebugService.warn('Trying with original email ID (no conversion)');
                                const originalEmailUrl = `https://graph.microsoft.com/v1.0/me/messages/${emailId}?$expand=attachments`;
                                DebugService.api('GET', originalEmailUrl);
                                const originalEmailRes = yield fetch(originalEmailUrl, {
                                    method: "GET",
                                    headers,
                                });
                                if (originalEmailRes.ok) {
                                    const originalEmail = yield originalEmailRes.json();
                                    DebugService.debug('Email retrieved successfully using original ID');
                                    return originalEmail;
                                }
                            }
                            catch (originalError) {
                                DebugService.warn('Original ID approach failed:', originalError);
                            }
                            // If that fails and we think it might be a shared mailbox, try shared mailbox endpoint
                            if (mailboxEmail && mailboxEmail !== Office.context.mailbox.userProfile.emailAddress) {
                                try {
                                    DebugService.warn('Trying with shared mailbox endpoint');
                                    const sharedEndpoint = this.getMailboxEndpoint(mailboxEmail, true);
                                    const sharedEmailUrl = `${sharedEndpoint}/messages/${restId}?$expand=attachments`;
                                    DebugService.api('GET', sharedEmailUrl);
                                    const sharedEmailRes = yield fetch(sharedEmailUrl, {
                                        method: "GET",
                                        headers,
                                    });
                                    if (sharedEmailRes.ok) {
                                        const sharedEmail = yield sharedEmailRes.json();
                                        DebugService.debug('Email retrieved successfully using shared mailbox endpoint');
                                        return sharedEmail;
                                    }
                                }
                                catch (fallbackError) {
                                    DebugService.warn('Shared mailbox endpoint also failed:', fallbackError);
                                }
                            }
                        }
                    }
                    // Handle 500 errors that might be related to special characters in email ID
                    // Before throwing the final error, try one more approach
                    if (retry === 9) { // Last attempt
                        DebugService.warn('Final attempt - trying search-based approach');
                        try {
                            // Try to get the email using search instead of direct ID access
                            const searchUrl = `https://graph.microsoft.com/v1.0/me/messages?$filter=id eq '${restId}'&$expand=attachments&$top=1`;
                            DebugService.api('GET', searchUrl);
                            const searchRes = yield fetch(searchUrl, {
                                method: "GET",
                                headers,
                            });
                            if (searchRes.ok) {
                                const searchResult = yield searchRes.json();
                                if (searchResult.value && searchResult.value.length > 0) {
                                    DebugService.debug('Email found via search approach');
                                    return searchResult.value[0];
                                }
                            }
                            // If search also fails, try to get user info and compare with Office context
                            DebugService.warn('Search failed, checking user context mismatch');
                            try {
                                const userInfoUrl = 'https://graph.microsoft.com/v1.0/me';
                                const userRes = yield fetch(userInfoUrl, { method: 'GET', headers });
                                if (userRes.ok) {
                                    const userInfo = yield userRes.json();
                                    const officeUserEmail = Office.context.mailbox.userProfile.emailAddress;
                                    DebugService.warn('User context comparison:', {
                                        graphUserId: userInfo.id,
                                        graphUserEmail: userInfo.userPrincipalName,
                                        officeUserEmail: officeUserEmail,
                                        emailId: emailId,
                                        restId: restId
                                    });
                                    if (userInfo.userPrincipalName !== officeUserEmail) {
                                        DebugService.error('User context mismatch detected! Graph token is for different user than Office context');
                                    }
                                }
                            }
                            catch (userError) {
                                DebugService.warn('Failed to get user info for comparison:', userError);
                            }
                        }
                        catch (searchError) {
                            DebugService.warn('Search approach also failed:', searchError);
                        }
                    }
                    DebugService.error(`Failed to get email after ${retry + 1} attempts:`, (_d = err.error) === null || _d === void 0 ? void 0 : _d.message);
                    throw new Error(`Failed to get email: ${(_e = err.error) === null || _e === void 0 ? void 0 : _e.message} (Status: ${emailRes.status})`);
                }
                const originalEmail = yield emailRes.json();
                DebugService.debug(`Email retrieved successfully on attempt ${retry + 1}`);
                DebugService.debug('Email retrieved successfully');
                return originalEmail;
            }
            catch (error) {
                if (retry < 10) {
                    const delay = 5000; // 5 seconds between each retry
                    DebugService.warn(`Failed to get email, retrying in ${delay}ms... (attempt ${retry + 1}/10)`, error);
                    yield new Promise(resolve => setTimeout(resolve, delay));
                    return this.getEmailWithRetry(graphToken, emailId, retry + 1, mailboxEmail, isShared);
                }
                DebugService.error(`Failed to get email after ${retry + 1} attempts:`, error);
                throw error;
            }
        });
    }
    /**
     * Alternative method to get email using internetMessageId
     * This method searches for emails using the internetMessageId which is more stable
     */
    getEmailByInternetMessageId(graphToken_1, internetMessageId_1) {
        return __awaiter(this, arguments, void 0, function* (graphToken, internetMessageId, retry = 0, mailboxEmail, isShared) {
            const headers = this.getAuthHeaders(graphToken);
            try {
                DebugService.debug(`Attempting internetMessageId search for: ${internetMessageId} (attempt ${retry + 1}/3)`);
                // Use the correct endpoint based on mailbox type
                const baseEndpoint = mailboxEmail && isShared !== undefined
                    ? this.getMailboxEndpoint(mailboxEmail, isShared)
                    : 'https://graph.microsoft.com/v1.0/me';
                // Search for the email using internetMessageId
                const searchUrl = `${baseEndpoint}/messages?$filter=internetMessageId eq '${internetMessageId}'&$expand=attachments&$top=1`;
                DebugService.api('GET', searchUrl);
                const searchRes = yield fetch(searchUrl, {
                    method: "GET",
                    headers,
                });
                if (searchRes.ok) {
                    const searchResult = yield searchRes.json();
                    if (searchResult.value && searchResult.value.length > 0) {
                        DebugService.debug('Email found via internetMessageId search');
                        return searchResult.value[0];
                    }
                }
                throw new Error('Email not found via internetMessageId search');
            }
            catch (error) {
                if (retry < 3) {
                    const delay = 2000; // 2 seconds between retries for search
                    DebugService.warn(`InternetMessageId search failed, retrying in ${delay}ms... (attempt ${retry + 1}/3)`, error);
                    yield new Promise(resolve => setTimeout(resolve, delay));
                    return this.getEmailByInternetMessageId(graphToken, internetMessageId, retry + 1, mailboxEmail, isShared);
                }
                DebugService.error(`InternetMessageId search failed after ${retry + 1} attempts:`, error);
                throw error;
            }
        });
    }
    /**
     * Alternative method to get email when direct ID access fails
     * This method tries to find the email using search criteria
     */
    getEmailBySearchFallback(graphToken_1, emailId_1) {
        return __awaiter(this, arguments, void 0, function* (graphToken, emailId, retry = 0, mailboxEmail, isShared) {
            const headers = this.getAuthHeaders(graphToken);
            try {
                DebugService.debug(`Attempting search fallback for emailId: ${emailId} (attempt ${retry + 1}/3)`);
                // Use the correct endpoint based on mailbox type
                const baseEndpoint = mailboxEmail && isShared !== undefined
                    ? this.getMailboxEndpoint(mailboxEmail, isShared)
                    : 'https://graph.microsoft.com/v1.0/me';
                // Try to search for the email in drafts folder first
                const searchUrl = `${baseEndpoint}/mailFolders('drafts')/messages?$filter=id eq '${emailId}'&$top=1`;
                DebugService.api('GET', searchUrl);
                const searchRes = yield fetch(searchUrl, {
                    method: "GET",
                    headers,
                });
                if (searchRes.ok) {
                    const searchResult = yield searchRes.json();
                    if (searchResult.value && searchResult.value.length > 0) {
                        DebugService.debug('Email found via search fallback in drafts folder');
                        return searchResult.value[0];
                    }
                }
                // If not found in drafts, try inbox
                const inboxSearchUrl = `${baseEndpoint}/mailFolders('inbox')/messages?$filter=id eq '${emailId}'&$top=1`;
                DebugService.api('GET', inboxSearchUrl);
                const inboxSearchRes = yield fetch(inboxSearchUrl, {
                    method: "GET",
                    headers,
                });
                if (inboxSearchRes.ok) {
                    const inboxSearchResult = yield inboxSearchRes.json();
                    if (inboxSearchResult.value && inboxSearchResult.value.length > 0) {
                        DebugService.debug('Email found via search fallback in inbox');
                        return inboxSearchResult.value[0];
                    }
                }
                throw new Error('Email not found via search fallback');
            }
            catch (error) {
                if (retry < 3) {
                    const delay = 2000; // 2 seconds between retries for search
                    DebugService.warn(`Search fallback failed, retrying in ${delay}ms... (attempt ${retry + 1}/3)`, error);
                    yield new Promise(resolve => setTimeout(resolve, delay));
                    return this.getEmailBySearchFallback(graphToken, emailId, retry + 1, mailboxEmail, isShared);
                }
                DebugService.error(`Search fallback failed after ${retry + 1} attempts:`, error);
                throw error;
            }
        });
    }
    forwardEmailBySearchFallback(graphToken, subject, sender, createdDateTime, uwwbID, sharedMailbox) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                DebugService.service('GraphEmailService', 'forwardEmailBySearchFallback started');
                DebugService.object('Search fallback parameters', { subject, sender, createdDateTime, uwwbID, sharedMailbox });
                // Validate parameters before using them
                DebugService.debug('Validating parameters:', {
                    subject: subject,
                    subjectType: typeof subject,
                    sender: sender,
                    senderType: typeof sender
                });
                if (!subject || typeof subject !== 'string') {
                    DebugService.error('Invalid subject parameter:', { subject, type: typeof subject });
                    throw new Error(`Subject is required and must be a string. Got: ${typeof subject} - ${subject}`);
                }
                if (!sender || typeof sender !== 'string') {
                    DebugService.error('Invalid sender parameter:', { sender, type: typeof sender });
                    throw new Error(`Sender is required and must be a string. Got: ${typeof sender} - ${sender}`);
                }
                const headers = this.getAuthHeaders(graphToken);
                // Search in drafts folder with subject and createdDate filtering
                DebugService.debug('About to call replace on subject and sender');
                const escapedSubject = subject.replace(/'/g, "''");
                const escapedSender = sender.replace(/'/g, "''");
                DebugService.debug('Successfully escaped subject and sender:', { escapedSubject, escapedSender });
                // Use $filter with subject, sender, and createdDateTime for exact match
                const filterQuery = `subject eq '${escapedSubject}' and from/emailAddress/address eq '${escapedSender}' and createdDateTime ge ${createdDateTime}`;
                const searchUrl = `https://graph.microsoft.com/v1.0/me/mailFolders('drafts')/messages?$filter=${encodeURIComponent(filterQuery)}&$orderby=createdDateTime desc&$top=1`;
                DebugService.api('GET', searchUrl);
                DebugService.debug('Search fallback query:', filterQuery);
                const searchResult = yield this.searchWithRetry(graphToken, searchUrl);
                if (!searchResult.value || searchResult.value.length === 0) {
                    throw new Error('Email not found in drafts folder with subject and date criteria');
                }
                const emailId = searchResult.value[0].id;
                DebugService.debug('Found email ID by search fallback:', emailId);
                // Forward the found email
                yield this.forwardEmailWithGraphToken(graphToken, {
                    emailId,
                    sharedMailbox,
                    uwwbID
                });
            }
            catch (error) {
                DebugService.errorWithStack('Email forwarding by search fallback failed', error);
                throw error;
            }
        });
    }
    forwardEmailByConversationId(graphToken, conversationId, uwwbID, sharedMailbox) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                DebugService.service('GraphEmailService', 'forwardEmailByConversationId started');
                DebugService.object('ConversationId parameters', { conversationId, uwwbID, sharedMailbox });
                const headers = this.getAuthHeaders(graphToken);
                // Search for email in drafts folder with retry for timing
                const searchUrl = `https://graph.microsoft.com/v1.0/me/mailFolders('drafts')/messages?$filter=conversationId eq '${conversationId}'&$top=1`;
                DebugService.api('GET', searchUrl);
                const searchResult = yield this.searchWithRetry(graphToken, searchUrl);
                if (!searchResult.value || searchResult.value.length === 0) {
                    throw new Error('Email not found in drafts folder with conversationId');
                }
                const emailId = searchResult.value[0].id;
                DebugService.debug('Found email ID by conversationId:', emailId);
                // Forward the found email
                yield this.forwardEmailWithGraphToken(graphToken, { emailId, sharedMailbox, uwwbID });
            }
            catch (error) {
                DebugService.errorWithStack('Email forwarding by conversationId failed', error);
                throw error;
            }
        });
    }
    // Helper method to search with retry for timing issues
    searchWithRetry(graphToken_1, searchUrl_1) {
        return __awaiter(this, arguments, void 0, function* (graphToken, searchUrl, retry = 0) {
            var _a, _b, _c;
            const headers = this.getAuthHeaders(graphToken);
            try {
                DebugService.debug(`Attempting to search (attempt ${retry + 1}/10)`);
                const searchRes = yield fetch(searchUrl, {
                    method: "GET",
                    headers,
                });
                if (!searchRes.ok) {
                    const err = yield searchRes.json();
                    // If it's a 404 or empty results and we're in retry mode, wait and retry
                    if ((searchRes.status === 404 || (searchRes.status === 200 && ((_a = err.value) === null || _a === void 0 ? void 0 : _a.length) === 0)) && retry < 10) {
                        const delay = 5000; // 5 seconds between each retry
                        DebugService.warn(`Search returned no results, retrying in ${delay}ms... (attempt ${retry + 1}/10)`);
                        yield new Promise(resolve => setTimeout(resolve, delay));
                        return this.searchWithRetry(graphToken, searchUrl, retry + 1);
                    }
                    DebugService.error(`Failed to search after ${retry + 1} attempts:`, (_b = err.error) === null || _b === void 0 ? void 0 : _b.message);
                    throw new Error(`Failed to search for email: ${(_c = err.error) === null || _c === void 0 ? void 0 : _c.message}`);
                }
                const searchResult = yield searchRes.json();
                DebugService.debug(`Search completed successfully on attempt ${retry + 1}`);
                DebugService.debug('Search completed successfully');
                return searchResult;
            }
            catch (error) {
                if (retry < 10) {
                    const delay = 5000; // 5 seconds between each retry
                    DebugService.warn(`Search failed, retrying in ${delay}ms... (attempt ${retry + 1}/10)`, error);
                    yield new Promise(resolve => setTimeout(resolve, delay));
                    return this.searchWithRetry(graphToken, searchUrl, retry + 1);
                }
                DebugService.error(`Failed to search after ${retry + 1} attempts:`, error);
                throw error;
            }
        });
    }
}
export default GraphEmailService.getInstance();
